import React from "react";
import { Box, Button, Typography, Stack } from "@mui/material";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { format, addDays, startOfWeek } from "date-fns";
import { useTranslation } from "react-i18next";

interface WeekNavigationProps {
  today: Date;
  currentWeekStart: Date;
  setCurrentWeekStart: (_date: Date) => void;
  setActiveDay: (_dayName: string) => void;
  weekDays: { dayName: string }[];
  isMonthView?: boolean;
  setIsMonthView?: (_val: boolean) => void;
  setForceSelectToday?: (_val: boolean) => void;
}

const WeekNavigation: React.FC<WeekNavigationProps> = ({
  today,
  currentWeekStart,
  setCurrentWeekStart,
  setActiveDay,
  isMonthView = false,
  setIsMonthView,
  setForceSelectToday,
}) => {
  const { t } = useTranslation();

  const goToPrevious = () => {
    if (isMonthView) {
      const prevMonth = new Date(currentWeekStart);
      prevMonth.setMonth(prevMonth.getMonth() - 1);
      const firstDayPrevMonth = new Date(
        prevMonth.getFullYear(),
        prevMonth.getMonth(),
        1
      );
      setCurrentWeekStart(firstDayPrevMonth);
    } else {
      setCurrentWeekStart(addDays(currentWeekStart, -7));
    }
  };
  const goToNext = () => {
    if (isMonthView) {
      const nextMonth = new Date(currentWeekStart);
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      const firstDayNextMonth = new Date(
        nextMonth.getFullYear(),
        nextMonth.getMonth(),
        1
      );
      setCurrentWeekStart(firstDayNextMonth);
    } else {
      setCurrentWeekStart(addDays(currentWeekStart, 7));
    }
  };
  const goToCurrent = () => {
    if (isMonthView) {
      const today = new Date();
      const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      setCurrentWeekStart(monthStart);
      // Force select today's date when Today button is clicked
      setActiveDay(format(today, "EEE"));
      if (setForceSelectToday) {
        setForceSelectToday(true);
      }
    } else {
      const weekStart = startOfWeek(today, { weekStartsOn: 1 });
      setCurrentWeekStart(weekStart);
      const todayName = format(today, "EEE");
      setActiveDay(todayName);
    }
  };

  const handleToggleView = () => {
    if (setIsMonthView) {
      if (!isMonthView) {
        const monthStart = new Date(
          currentWeekStart.getFullYear(),
          currentWeekStart.getMonth(),
          1
        );
        setCurrentWeekStart(monthStart);
      }
      setIsMonthView(!isMonthView);
    }
  };

  return (
    <>
      {/* Desktop: >= 768px (sm) */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems="center"
        justifyContent="center"
        sx={{ display: { xs: "none", sm: "flex" } }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Button
            variant="outlined"
            onClick={goToCurrent}
            sx={{
              ml: 1,
              fontWeight: "bold",
              textTransform: "none",
              color: "#e67c52",
              borderColor: "#e67c52",
              "&:hover": {
                borderColor: "#d95c2f",
                color: "#d95c2f",
              },
            }}
          >
            {t("navigation.today")}
          </Button>
          <Box sx={{ mx: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              {isMonthView
                ? format(currentWeekStart, "MMMM yyyy")
                : `${format(currentWeekStart, "MMMM d")}–${format(addDays(currentWeekStart, 6), "d, yyyy")}`}
            </Typography>
          </Box>
          <Button
            onClick={goToPrevious}
            sx={{
              minWidth: 40,
              width: 40,
              height: 40,
              borderRadius: "12px",
              marginRight: 2,
              backgroundColor: "#fff5f0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              "&:hover": {
                backgroundColor: "#ffe8e0",
              },
              border: "none",
              boxShadow: "none",
            }}
          >
            <ChevronLeft size={20} color="#e67c52" />
          </Button>
          <Button
            onClick={goToNext}
            sx={{
              minWidth: 40,
              width: 40,
              height: 40,
              borderRadius: "12px",
              backgroundColor: "#fff5f0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              "&:hover": {
                backgroundColor: "#ffe8e0",
              },
              border: "none",
              boxShadow: "none",
            }}
          >
            <ChevronRight size={20} color="#e67c52" />
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleToggleView}
            sx={{
              ml: 2,
              textTransform: "none",
              color: "#e67c52",
              borderColor: "#e67c52",
              borderRadius: "5px",
              fontWeight: "bold",
              "&:hover": {
                backgroundColor: "#ffe8e0",
                color: "#fff",
              },
            }}
          >
            {isMonthView ? t("appointment.week") : t("appointment.month")}
          </Button>
        </Box>
      </Stack>
      {/* Mobile: < 768px (xs) */}
      <Box
        sx={{
          display: { xs: "flex", sm: "none" },
          flexDirection: "column",
          gap: 1,
          mt: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Button
            color="primary"
            onClick={goToPrevious}
            sx={{
              minWidth: 48,
              width: 48,
              height: 40,
              borderRadius: "10px",
              backgroundColor: "#fff5f0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              "&:hover": {
                backgroundColor: "#ffe8e0",
              },
              border: "none",
              boxShadow: "none",
            }}
          >
            <Box sx={{ fontSize: 28, fontWeight: 700 }}>
              <ChevronLeft />
            </Box>
          </Button>
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            {isMonthView
              ? format(currentWeekStart, "MMMM yyyy")
              : `${format(currentWeekStart, "MMMM d")}–${format(addDays(currentWeekStart, 6), "d, yyyy")}`}
          </Typography>
          <Button
            color="primary"
            onClick={goToNext}
            sx={{
              minWidth: 48,
              width: 48,
              height: 40,
              borderRadius: "10px",
              backgroundColor: "#fff5f0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              "&:hover": {
                backgroundColor: "#ffe8e0",
              },
              border: "none",
              boxShadow: "none",
            }}
          >
            <Box sx={{ fontSize: 28, fontWeight: 700 }}>
              <ChevronRight />
            </Box>
          </Button>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Button
            onClick={goToCurrent}
            sx={{
              fontWeight: "bold",
              textTransform: "none",
              color: "#e67c52",
              borderColor: "#e67c52",
              "&:hover": {
                borderColor: "#d95c2f",
                color: "#d95c2f",
              },
            }}
          >
            {t("navigation.today")}
          </Button>
          <Button
            color="secondary"
            onClick={handleToggleView}
            sx={{
              textTransform: "none",
              color: "#e67c52",
              borderColor: "#e67c52",
              borderRadius: "5px",
              fontWeight: "bold",
              "&:hover": {
                backgroundColor: "#ffe8e0",
                color: "#fff",
              },
            }}
          >
            {isMonthView ? t("appointment.week") : t("appointment.month")}
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default WeekNavigation;
